import * as apiCompany from 'api/endpoints/company/company.api';
import * as CompanyTypes from './company.types';
import { notificationErr } from 'views/components/notification';
import { companyError } from 'utils/errorCodes';
import { messageError, messageSuccess } from 'views/components/message';
import { IGetExchangesAPIResponseItem } from 'api/endpoints/general/general.response.interface';
import {
  IaddSocialMediaAPIResponseItem,
  IupdateTenantDataAPIBody,
  IGetCompanyInfoAPIResponseData,
  ISocialMedia,
} from 'api/endpoints/company/company.response.interface';
import { ITranslate } from 'interfaces/general.interface';
import { ISAdminTenantState } from 'api/endpoints/superAdmin/superAdmin.response.interface';
import { TcompanyDispatchReducer } from './company.reducer';
import { ICompanyUpdateFields } from 'interfaces/company.interface';

export async function getCompanyDataAction({
  dispatch,
  tenantId,
  countryId,
  token,
  translate,
}: {
  dispatch: React.Dispatch<TcompanyDispatchReducer>;
  tenantId: string;
  countryId: string;
  token: string;
  translate: ITranslate;
}): Promise<boolean> {
  dispatch({ type: CompanyTypes.SET_IS_LOADING, payload: true });
  try {
    const [companyFetch] = await Promise.all([
      apiCompany.getCompanyInfoAPI({
        tenantId,
        countryId,
        token,
      }),
    ]);
    if (companyFetch.response.status === 200) {
      dispatch({
        type: CompanyTypes.GET_COMPANY_DATA,
        payload: companyFetch.data,
      });
      return true;
    }
    return false;
  } catch (err) {
    notificationErr(companyError.getCompanyData_001, translate);
    return false;
  } finally {
    dispatch({ type: CompanyTypes.SET_IS_LOADING, payload: false });
  }
}

export async function updateCompanyAction({
  dispatch,
  companyState,
  companyData,
  exchanges,
  token,
  translate,
}: {
  dispatch: React.Dispatch<TcompanyDispatchReducer>;
  companyState: IGetCompanyInfoAPIResponseData;
  companyData: ICompanyUpdateFields;
  exchanges: Array<IGetExchangesAPIResponseItem>;
  token: string;
  translate: ITranslate;
}) {
  const newSocialMedia = companyData.new_social_media;
  delete companyData.new_social_media;
  const body: IupdateTenantDataAPIBody = {
    filter: { id: companyState.id },
    update: companyData,
  };
  try {
    const [companyFetch, socialMediaFetch] = await Promise.all([
      apiCompany.updateTenantDataAPI({ body, token }),
      apiCompany.addSocialMediaAPI({
        tenantId: companyState.id,
        items: newSocialMedia as Array<ISocialMedia>,
        token,
      }),
    ]);
    if (
      socialMediaFetch.response.status === 200 &&
      (socialMediaFetch.data as Array<IaddSocialMediaAPIResponseItem>).length > 0
    ) {
      for (const item of socialMediaFetch.data as Array<IaddSocialMediaAPIResponseItem>) {
        const socialMediaItem: ISocialMedia = {
          id: item.id,
          name: item.name,
          url: item.url,
        };
        dispatch({
          type: CompanyTypes.ADD_SOCIAL_MEDIA,
          payload: socialMediaItem,
        });
      }
    }
    if (companyFetch.response.status === 200) {
      messageSuccess('Datos de la compañia actualizados correctamente.');
      dispatch({
        type: CompanyTypes.UPDATE_COMPANY,
        payload: {
          companyData,
          exchanges,
        },
      });
      return true;
    }
    if (companyFetch.response.status === 404) {
      messageError('No se ha encontrado al usuario.');
      notificationErr(companyError.update_company_003, translate);
      return false;
    }
    messageError('Error al actualizar los datos de empresa.');
    notificationErr(companyError.update_company_002, translate);
    return false;
  } catch (err) {
    messageError('Error al actualizar los datos de empresa.');
    notificationErr(companyError.update_company_001, translate);
    return false;
  }
}

export async function updateCompanyLogoAction({
  dispatch,
  tenantId,
  image,
  token,
  translate,
}: {
  dispatch: React.Dispatch<TcompanyDispatchReducer>;
  tenantId: string;
  image: File;
  token: string;
  translate: ITranslate;
}) {
  try {
    const fetch = await apiCompany.updateCompanyLogoAPI({ tenantId, image, token });
    if (fetch.response.status === 200) {
      messageSuccess('Logo de compañia actualizado correctamente.');
      dispatch({
        type: CompanyTypes.UPDATE_COMPANY_LOGO,
        payload: fetch.data.url,
      });
      return true;
    }
    messageError('Error al actualizar el logo de la compañia.');
    notificationErr(companyError.update_company_logo_002, translate);
    return false;
  } catch (err) {
    messageError('Error al actualizar el logo de la compañia.');
    notificationErr(companyError.update_company_logo_001, translate);
    return false;
  }
}

export async function updateSocialMediaAction({
  dispatch,
  companyState,
  socialMedia,
  token,
  translate,
}: {
  dispatch: React.Dispatch<TcompanyDispatchReducer>;
  companyState: IGetCompanyInfoAPIResponseData;
  socialMedia: ISocialMedia;
  token: string;
  translate: ITranslate;
}) {
  const { id, url, name } = socialMedia;
  try {
    const updateFetch = await apiCompany.updateSocialMediaAPI({
      tenant_id: companyState.id,
      id,
      url,
      name,
      token,
    });
    if (updateFetch.response.status === 200 && updateFetch.data[0] === 1) {
      messageSuccess('Red social actualizada correctamente.');
      const findSocialMedia = companyState.tenant_social_media.findIndex(
        (el) => el.id === id
      );
      companyState.tenant_social_media[findSocialMedia].url = url;
      companyState.tenant_social_media[findSocialMedia].name = name;
      dispatch({
        type: CompanyTypes.SET_SOCIAL_MEDIA,
        payload: companyState.tenant_social_media,
      });
      return true;
    } else {
      messageError('Error al actualizar la red social.');
      notificationErr(companyError.updateSocialMedia_002, translate);
      return false;
    }
  } catch (err) {
    messageError('Error al actualizar la red social.');
    notificationErr(companyError.updateSocialMedia_001, translate);
    return false;
  }
}

export async function removeSocialMediaAction({
  dispatch,
  companyState,
  socialMediaId,
  token,
  translate,
}: {
  dispatch: React.Dispatch<TcompanyDispatchReducer>;
  companyState: IGetCompanyInfoAPIResponseData;
  socialMediaId: string;
  token: string;
  translate: ITranslate;
}) {
  try {
    const removeFetch = await apiCompany.removeSocialMediaAPI({ socialMediaId, token });
    if (removeFetch.response.status === 200 && removeFetch.data === 1) {
      const filteredCompany = companyState.tenant_social_media.filter(
        (el) => el.id !== socialMediaId
      );
      messageSuccess('Red social eliminada correctamente.');
      dispatch({
        type: CompanyTypes.SET_SOCIAL_MEDIA,
        payload: filteredCompany,
      });
      return true;
    } else {
      messageError('Error al eliminar la red social.');
      notificationErr(companyError.removeSocialMedia_002, translate);
      return false;
    }
  } catch (err) {
    notificationErr(companyError.removeSocialMedia_001, translate);
    return false;
  }
}

export async function selectCompanySuperAdminAction({
  dispatch,
  tenant,
  translate,
  token,
}: {
  dispatch: React.Dispatch<TcompanyDispatchReducer>;
  tenant: ISAdminTenantState;
  translate: ITranslate;
  token: string;
}) {
  if (!tenant) {
    notificationErr(companyError.selectCompanySuperAdmin_003, translate);
    return false;
  }
  try {
    const [companyFetch] = await Promise.all([
      apiCompany.getCompanyInfoAPI({
        tenantId: tenant.id,
        countryId: tenant.country.id,
        token,
      }),
    ]);
    if (companyFetch.response.status === 200) {
      dispatch({
        type: CompanyTypes.LOGIN_COMPANY_SUPER_ADMIN,
        payload: companyFetch.data,
      });
      return true;
    }
    notificationErr(companyError.selectCompanySuperAdmin_002, translate);
    return false;
  } catch (err) {
    notificationErr(companyError.selectCompanySuperAdmin_001, translate);
    return false;
  } finally {
    dispatch({ type: CompanyTypes.SET_IS_LOADING, payload: false });
  }
}
